/**
 * Catalogue des profils sectoriels et usages typiques par code NAF.
 *
 * Source : nomenclature NAF rév. 2 (2008). On résout un profil par code complet
 * en priorité, sinon par préfixe 4, 3 ou 2 caractères. Le profil détermine la liste
 * d'équipements / postes consommateurs proposés à l'utilisateur dans le
 * comparateur électricité/gaz (étape « Usages »).
 *
 * Le fichier est isomorphique (importé client + serveur) : aucune dépendance
 * runtime (pas d'icône React, pas de zod, juste des constantes typées).
 */

export type UsageEnergy = 'electricity' | 'gas' | 'both';

/**
 * Clés d'icônes. Mappées en composants Lucide côté front
 * (cf. `client/src/components/tools/ComparateurFunnel/usage-icons.ts`).
 */
export type UsageIconKey =
  | 'flame'
  | 'zap'
  | 'fire'
  | 'sun'
  | 'snowflake'
  | 'thermometer'
  | 'refrigerator'
  | 'chef-hat'
  | 'utensils'
  | 'lightbulb'
  | 'droplet'
  | 'wind'
  | 'wrench'
  | 'cog'
  | 'printer'
  | 'scissors'
  | 'shirt'
  | 'washing-machine'
  | 'car'
  | 'fuel'
  | 'spray-can'
  | 'monitor'
  | 'server'
  | 'briefcase'
  | 'cup'
  | 'pizza'
  | 'cake'
  | 'leaf'
  | 'tractor'
  | 'wheat'
  | 'sprout'
  | 'milk'
  | 'egg'
  | 'fish'
  | 'beef'
  | 'wine'
  | 'beer'
  | 'flower'
  | 'pill'
  | 'stethoscope'
  | 'baby'
  | 'graduation-cap'
  | 'dumbbell'
  | 'hammer'
  | 'pump'
  | 'air-vent'
  | 'building';

export interface SectorUsage {
  /** Identifiant stable, persisté en DB. */
  id: string;
  /** Libellé court affiché dans la carte (≤ 32 caractères idéal). */
  label: string;
  /** Sous-titre / aide contextuelle (1 phrase). */
  hint?: string;
  /** Énergie concernée. `both` = la même question vaut pour élec et gaz. */
  energy: UsageEnergy;
  iconKey: UsageIconKey;
  /** Pré-coché par défaut si le profil est très probable. */
  recommended?: boolean;
}

export interface SectorProfile {
  /** Identifiant stable, persisté en DB sous `sector_profile`. */
  id: string;
  /** Libellé affichable dans le funnel et le récap. */
  label: string;
  /** Court paragraphe contextuel (1 phrase, ton client). */
  description: string;
  iconKey: UsageIconKey;
  /** Liste d'usages proposés (toggle multi). Ordonnée par pertinence. */
  usages: SectorUsage[];
}

/* -------------------------------------------------------------------------- */
/*  Profils sectoriels                                                         */
/* -------------------------------------------------------------------------- */

const BOULANGER: SectorProfile = {
  id: 'boulanger',
  label: 'Boulangerie / Pâtisserie',
  description:
    "Cuisson, chambre de pousse, vitrines réfrigérées : un profil très consommateur, surtout aux heures de pointe.",
  iconKey: 'cake',
  usages: [
    {
      id: 'four_gaz',
      label: 'Four à pain au gaz',
      hint: 'Four rotatif, four à sole, four à pizza à gaz.',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'four_elec',
      label: 'Four à pain électrique',
      hint: 'Four à étage électrique, four ventilé.',
      energy: 'electricity',
      iconKey: 'zap',
    },
    {
      id: 'chambre_pousse',
      label: 'Chambre de pousse / fermentation',
      hint: 'Régulation chaud/froid pour la pâte.',
      energy: 'electricity',
      iconKey: 'thermometer',
      recommended: true,
    },
    {
      id: 'vitrine_froide',
      label: 'Vitrines réfrigérées',
      hint: 'Pâtisseries, sandwichs, boissons fraîches.',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambre froide',
      hint: 'Stockage matières premières.',
      energy: 'electricity',
      iconKey: 'snowflake',
    },
    {
      id: 'eclairage_vitrine',
      label: 'Éclairage vitrine + magasin',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'ecs',
      label: 'Eau chaude (lavage, plonge)',
      hint: 'Ballon électrique ou production gaz.',
      energy: 'both',
      iconKey: 'droplet',
    },
    {
      id: 'clim_local',
      label: 'Climatisation magasin',
      energy: 'electricity',
      iconKey: 'wind',
    },
  ],
};

const RESTAURANT: SectorProfile = {
  id: 'restaurant',
  label: 'Restaurant / Brasserie',
  description:
    "Cuisson, plonge, froid alimentaire et climatisation : profil multi-postes typique de la restauration.",
  iconKey: 'utensils',
  usages: [
    {
      id: 'piano_gaz',
      label: 'Piano de cuisson au gaz',
      hint: 'Feux vifs, plaques coup-de-feu, salamandre gaz.',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'piano_induction',
      label: 'Piano induction / vitrocéramique',
      energy: 'electricity',
      iconKey: 'zap',
    },
    {
      id: 'four_resto',
      label: 'Four de cuisson (mixte, à air pulsé)',
      energy: 'both',
      iconKey: 'chef-hat',
      recommended: true,
    },
    {
      id: 'friteuse',
      label: 'Friteuse(s)',
      energy: 'both',
      iconKey: 'fire',
    },
    {
      id: 'lave_vaisselle_pro',
      label: 'Lave-vaisselle professionnel',
      hint: 'Capot, tunnel ou frontal — gros consommateur d’ECS.',
      energy: 'electricity',
      iconKey: 'washing-machine',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambre froide positive + négative',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'vitrine_froide',
      label: 'Vitrines réfrigérées (boissons, desserts)',
      energy: 'electricity',
      iconKey: 'snowflake',
    },
    {
      id: 'ecs_resto',
      label: 'Eau chaude sanitaire (cuisine + sanitaires)',
      energy: 'both',
      iconKey: 'droplet',
    },
    {
      id: 'extraction_hotte',
      label: 'Hotte d’extraction',
      hint: 'Moteur d’extraction au-dessus du piano.',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
    {
      id: 'clim_salle',
      label: 'Climatisation de la salle',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'chauffage_terrasse',
      label: 'Chauffage de terrasse (gaz)',
      hint: 'Parasols chauffants au propane / gaz naturel.',
      energy: 'gas',
      iconKey: 'flame',
    },
  ],
};

const RESTAURATION_RAPIDE: SectorProfile = {
  id: 'restauration_rapide',
  label: 'Restauration rapide / Snack',
  description: "Friteuses, plaques, vitrine chaude et vitrine froide : profil tout électrique le plus souvent.",
  iconKey: 'pizza',
  usages: [
    {
      id: 'friteuse_elec',
      label: 'Friteuse(s) électrique(s)',
      energy: 'electricity',
      iconKey: 'fire',
      recommended: true,
    },
    {
      id: 'plaque_snack',
      label: 'Plaque de cuisson / grill',
      energy: 'both',
      iconKey: 'utensils',
      recommended: true,
    },
    {
      id: 'four_pizza_gaz',
      label: 'Four à pizza au gaz',
      energy: 'gas',
      iconKey: 'flame',
    },
    {
      id: 'four_pizza_elec',
      label: 'Four à pizza électrique',
      energy: 'electricity',
      iconKey: 'zap',
    },
    {
      id: 'vitrine_chaude',
      label: 'Vitrine chaude / bain-marie',
      energy: 'electricity',
      iconKey: 'fire',
    },
    {
      id: 'vitrine_froide',
      label: 'Vitrine réfrigérée boissons',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'extraction_hotte',
      label: 'Hotte d’extraction',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
    {
      id: 'eclairage_enseigne',
      label: 'Enseigne lumineuse + éclairage',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const TRAITEUR: SectorProfile = {
  id: 'traiteur',
  label: 'Traiteur / Restauration collective',
  description: "Production en cuisine centrale, cellule de refroidissement, livraison réfrigérée.",
  iconKey: 'chef-hat',
  usages: [
    {
      id: 'piano_gaz',
      label: 'Cuisson au gaz (sauteuses, marmites)',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'four_resto',
      label: 'Four mixte / four à vapeur',
      energy: 'both',
      iconKey: 'chef-hat',
      recommended: true,
    },
    {
      id: 'cellule_refroidissement',
      label: 'Cellule de refroidissement rapide',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambres froides + négatives',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'lave_vaisselle_pro',
      label: 'Tunnel de lavage / lave-batterie',
      energy: 'electricity',
      iconKey: 'washing-machine',
    },
    {
      id: 'ecs_resto',
      label: 'Eau chaude (lavage / plonge)',
      energy: 'both',
      iconKey: 'droplet',
    },
    {
      id: 'extraction_hotte',
      label: 'Hottes + extracteurs',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
  ],
};

const BAR_CAFE: SectorProfile = {
  id: 'bar_cafe',
  label: 'Bar / Café / Brasserie',
  description: "Tireuses, machines à café, vitrines froides, ambiance lumineuse.",
  iconKey: 'cup',
  usages: [
    {
      id: 'machine_cafe',
      label: 'Machine à café professionnelle',
      energy: 'electricity',
      iconKey: 'cup',
      recommended: true,
    },
    {
      id: 'tireuse_biere',
      label: 'Tireuse à bière + groupe froid',
      energy: 'electricity',
      iconKey: 'beer',
      recommended: true,
    },
    {
      id: 'vitrine_froide',
      label: 'Vitrines / armoires réfrigérées',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'lave_verre',
      label: 'Lave-verre / lave-vaisselle',
      energy: 'electricity',
      iconKey: 'washing-machine',
    },
    {
      id: 'clim_salle',
      label: 'Climatisation',
      energy: 'electricity',
      iconKey: 'wind',
    },
    {
      id: 'chauffage_terrasse',
      label: 'Chauffage de terrasse au gaz',
      energy: 'gas',
      iconKey: 'flame',
    },
    {
      id: 'eclairage_ambiance',
      label: 'Éclairage d’ambiance',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const HOTEL: SectorProfile = {
  id: 'hotel',
  label: 'Hôtel / Hébergement',
  description: "Chauffage, eau chaude sanitaire, climatisation, blanchisserie, restauration intégrée.",
  iconKey: 'building',
  usages: [
    {
      id: 'chauffage_gaz',
      label: 'Chaudière gaz collective',
      hint: 'Chauffage + ECS chambres et communs.',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'chauffage_elec',
      label: 'Chauffage électrique en chambres',
      energy: 'electricity',
      iconKey: 'zap',
    },
    {
      id: 'pac',
      label: 'Pompe à chaleur (PAC)',
      energy: 'electricity',
      iconKey: 'thermometer',
    },
    {
      id: 'ecs_hotel',
      label: 'Production d’eau chaude sanitaire',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'clim_chambres',
      label: 'Climatisation des chambres',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'piscine',
      label: 'Piscine / spa chauffés',
      energy: 'both',
      iconKey: 'droplet',
    },
    {
      id: 'cuisine_hotel',
      label: 'Cuisine restaurant intégrée',
      energy: 'both',
      iconKey: 'chef-hat',
    },
    {
      id: 'blanchisserie',
      label: 'Blanchisserie interne',
      hint: 'Machines à laver, sèche-linge, calandre.',
      energy: 'both',
      iconKey: 'washing-machine',
    },
    {
      id: 'ascenseur',
      label: 'Ascenseur(s)',
      energy: 'electricity',
      iconKey: 'building',
    },
    {
      id: 'eclairage_communs',
      label: 'Éclairage parties communes',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
  ],
};

const BOUCHERIE: SectorProfile = {
  id: 'boucherie_charcuterie',
  label: 'Boucherie / Charcuterie',
  description: "Vitrines réfrigérées, chambres froides, laboratoire de découpe.",
  iconKey: 'beef',
  usages: [
    {
      id: 'vitrine_froide',
      label: 'Vitrines réfrigérées',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambres froides positives',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'congelation',
      label: 'Congélateur / chambre négative',
      energy: 'electricity',
      iconKey: 'snowflake',
    },
    {
      id: 'four_charcuterie',
      label: 'Four de cuisson (rôtisserie, jambon)',
      energy: 'both',
      iconKey: 'chef-hat',
    },
    {
      id: 'rotissoire',
      label: 'Rôtissoire électrique ou gaz',
      energy: 'both',
      iconKey: 'fire',
    },
    {
      id: 'machine_decoupe',
      label: 'Machines de découpe / hachoir',
      energy: 'electricity',
      iconKey: 'cog',
    },
    {
      id: 'eclairage_vitrine',
      label: 'Éclairage vitrine + magasin',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
  ],
};

const POISSONNERIE: SectorProfile = {
  id: 'poissonnerie',
  label: 'Poissonnerie',
  description: "Étals glacés, vitrines réfrigérées, viviers.",
  iconKey: 'fish',
  usages: [
    {
      id: 'machine_glace',
      label: 'Machine à glace écaille',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'vitrine_froide',
      label: 'Vitrines réfrigérées',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambres froides',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'vivier',
      label: 'Vivier / aquarium (crustacés)',
      energy: 'electricity',
      iconKey: 'fish',
    },
    {
      id: 'eclairage_vitrine',
      label: 'Éclairage vitrine',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const ALIMENTATION_GENERALE: SectorProfile = {
  id: 'alimentation_generale',
  label: 'Supérette / Alimentation générale',
  description: "Linéaires froids, chambres froides, éclairage de surface, climatisation.",
  iconKey: 'refrigerator',
  usages: [
    {
      id: 'meubles_froid_positif',
      label: 'Meubles froid positif (vitrines, bacs)',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'meubles_froid_negatif',
      label: 'Meubles froid négatif (surgelés)',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambres froides arrière-magasin',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'eclairage_surface',
      label: 'Éclairage surface de vente',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'clim_local',
      label: 'Climatisation',
      energy: 'electricity',
      iconKey: 'wind',
    },
    {
      id: 'four_traiteur',
      label: 'Espace traiteur (four, rôtissoire)',
      energy: 'both',
      iconKey: 'chef-hat',
    },
  ],
};

const FLEURISTE: SectorProfile = {
  id: 'fleuriste',
  label: 'Fleuriste',
  description: "Chambre froide pour les fleurs coupées, éclairage et chauffage doux.",
  iconKey: 'flower',
  usages: [
    {
      id: 'chambre_froide_fleurs',
      label: 'Chambre froide fleurs coupées',
      energy: 'electricity',
      iconKey: 'snowflake',
      recommended: true,
    },
    {
      id: 'eclairage_vitrine',
      label: 'Éclairage vitrine + magasin',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'chauffage_boutique',
      label: 'Chauffage boutique',
      energy: 'both',
      iconKey: 'thermometer',
    },
    {
      id: 'humidificateur',
      label: 'Humidificateur / vaporisateur',
      energy: 'electricity',
      iconKey: 'droplet',
    },
  ],
};

const PHARMACIE: SectorProfile = {
  id: 'pharmacie',
  label: 'Pharmacie',
  description: "Réfrigération des médicaments, éclairage, climatisation, bureautique.",
  iconKey: 'pill',
  usages: [
    {
      id: 'refri_medicaments',
      label: 'Réfrigérateurs médicaments',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'eclairage_officine',
      label: 'Éclairage officine',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'clim_local',
      label: 'Climatisation (zone médicaments)',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'bureautique',
      label: 'Postes informatiques + serveur',
      energy: 'electricity',
      iconKey: 'monitor',
    },
    {
      id: 'preparatoire',
      label: 'Préparatoire / robot de stockage',
      energy: 'electricity',
      iconKey: 'cog',
    },
  ],
};

const COIFFEUR: SectorProfile = {
  id: 'coiffeur',
  label: 'Salon de coiffure',
  description: "Bacs à shampoing (ECS), sèche-cheveux, ambiance lumineuse et chauffée.",
  iconKey: 'scissors',
  usages: [
    {
      id: 'ecs_bac',
      label: 'Eau chaude pour bacs à shampoing',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'seche_cheveux',
      label: 'Sèche-cheveux + casques',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'chauffage_salon',
      label: 'Chauffage salon',
      energy: 'both',
      iconKey: 'thermometer',
    },
    {
      id: 'clim_local',
      label: 'Climatisation',
      energy: 'electricity',
      iconKey: 'wind',
    },
    {
      id: 'eclairage_miroirs',
      label: 'Éclairage miroirs + ambiance',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
  ],
};

const ESTHETIQUE: SectorProfile = {
  id: 'esthetique',
  label: 'Institut de beauté / Spa',
  description: "Cabines chauffées, vapeur, lampes UV, hammam ou jacuzzi éventuels.",
  iconKey: 'spray-can',
  usages: [
    {
      id: 'chauffage_cabine',
      label: 'Chauffage cabines (radiants infrarouge)',
      energy: 'electricity',
      iconKey: 'thermometer',
      recommended: true,
    },
    {
      id: 'ecs_cabine',
      label: 'Eau chaude (lavabos, douches)',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'lampe_uv',
      label: 'Lampes UV / LED esthétique',
      energy: 'electricity',
      iconKey: 'sun',
    },
    {
      id: 'hammam_sauna',
      label: 'Hammam / sauna',
      energy: 'electricity',
      iconKey: 'thermometer',
    },
    {
      id: 'eclairage_cabine',
      label: 'Éclairage cabine + accueil',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const PRESSING: SectorProfile = {
  id: 'pressing',
  label: 'Pressing / Laverie',
  description: "Lave-linge, sèche-linge, calandre, centrale vapeur : profil très consommateur.",
  iconKey: 'washing-machine',
  usages: [
    {
      id: 'lave_linge_pro',
      label: 'Lave-linge professionnel',
      energy: 'both',
      iconKey: 'washing-machine',
      recommended: true,
    },
    {
      id: 'seche_linge_pro',
      label: 'Sèche-linge professionnel',
      energy: 'both',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'centrale_vapeur',
      label: 'Centrale vapeur / fer pro',
      energy: 'both',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'calandre',
      label: 'Calandre repasseuse',
      energy: 'both',
      iconKey: 'shirt',
    },
    {
      id: 'machine_sec',
      label: 'Machine de nettoyage à sec',
      energy: 'electricity',
      iconKey: 'cog',
    },
    {
      id: 'extraction_buanderie',
      label: 'Extraction d’air (buanderie)',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
  ],
};

const GARAGE: SectorProfile = {
  id: 'garage',
  label: 'Garage / Carrosserie',
  description: "Compresseur, ponts élévateurs, cabine de peinture, éclairage atelier.",
  iconKey: 'car',
  usages: [
    {
      id: 'compresseur_air',
      label: 'Compresseur d’air',
      energy: 'electricity',
      iconKey: 'pump',
      recommended: true,
    },
    {
      id: 'pont_elev',
      label: 'Ponts élévateurs',
      energy: 'electricity',
      iconKey: 'wrench',
      recommended: true,
    },
    {
      id: 'cabine_peinture',
      label: 'Cabine de peinture (chauffage + extraction)',
      hint: 'Souvent au gaz pour le chauffage de cuisson.',
      energy: 'both',
      iconKey: 'spray-can',
    },
    {
      id: 'chauffage_atelier',
      label: 'Chauffage atelier (radiants gaz, aérothermes)',
      energy: 'gas',
      iconKey: 'flame',
    },
    {
      id: 'eclairage_atelier',
      label: 'Éclairage atelier',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'outillage_elec',
      label: 'Outillage électrique (visseuses, postes à souder)',
      energy: 'electricity',
      iconKey: 'cog',
    },
    {
      id: 'tunnel_lavage',
      label: 'Tunnel de lavage / portique',
      energy: 'both',
      iconKey: 'droplet',
    },
  ],
};

const STATION_SERVICE: SectorProfile = {
  id: 'station_service',
  label: 'Station-service',
  description: "Pompes, éclairage 24/7 sous auvent, boutique avec froid commercial.",
  iconKey: 'fuel',
  usages: [
    {
      id: 'pompe_carburant',
      label: 'Pompes de distribution',
      energy: 'electricity',
      iconKey: 'fuel',
      recommended: true,
    },
    {
      id: 'eclairage_auvent',
      label: 'Éclairage auvent (24/7)',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'meubles_froid_positif',
      label: 'Vitrines froides boutique',
      energy: 'electricity',
      iconKey: 'refrigerator',
    },
    {
      id: 'machine_cafe',
      label: 'Coin café / boissons chaudes',
      energy: 'electricity',
      iconKey: 'cup',
    },
    {
      id: 'tunnel_lavage',
      label: 'Tunnel / portique de lavage',
      energy: 'both',
      iconKey: 'droplet',
    },
  ],
};

const IMPRIMERIE: SectorProfile = {
  id: 'imprimerie',
  label: 'Imprimerie / Reprographie',
  description: "Presses offset/numériques, machines de pliage, séchage UV, ambiance régulée.",
  iconKey: 'printer',
  usages: [
    {
      id: 'presse_offset',
      label: 'Presses offset / numériques',
      energy: 'electricity',
      iconKey: 'printer',
      recommended: true,
    },
    {
      id: 'sechage_uv',
      label: 'Séchage UV / four de séchage',
      energy: 'both',
      iconKey: 'sun',
    },
    {
      id: 'compresseur_air',
      label: 'Compresseur d’air',
      energy: 'electricity',
      iconKey: 'pump',
    },
    {
      id: 'clim_atelier',
      label: 'Climatisation atelier (hygrométrie)',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'eclairage_atelier',
      label: 'Éclairage atelier',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const ATELIER_INDUSTRIE: SectorProfile = {
  id: 'atelier_industrie',
  label: 'Atelier industriel / Usine',
  description: "Machines de production, compresseur, chauffage de halle, éclairage atelier.",
  iconKey: 'cog',
  usages: [
    {
      id: 'process_machine',
      label: 'Machines de production',
      energy: 'electricity',
      iconKey: 'cog',
      recommended: true,
    },
    {
      id: 'compresseur_air',
      label: 'Compresseur d’air',
      energy: 'electricity',
      iconKey: 'pump',
      recommended: true,
    },
    {
      id: 'four_industriel',
      label: 'Four industriel / traitement thermique',
      energy: 'both',
      iconKey: 'fire',
    },
    {
      id: 'chauffage_atelier',
      label: 'Chauffage halle (radiants gaz / aérothermes)',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'eclairage_atelier',
      label: 'Éclairage atelier',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'extraction_atelier',
      label: 'Extraction / ventilation',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
    {
      id: 'soudure',
      label: 'Postes à souder',
      energy: 'electricity',
      iconKey: 'hammer',
    },
  ],
};

const MENUISERIE: SectorProfile = {
  id: 'menuiserie',
  label: 'Menuiserie / Atelier bois',
  description: "Machines à bois, aspiration centralisée, chauffage de halle.",
  iconKey: 'hammer',
  usages: [
    {
      id: 'machine_bois',
      label: 'Machines à bois (scies, raboteuses)',
      energy: 'electricity',
      iconKey: 'cog',
      recommended: true,
    },
    {
      id: 'compresseur_air',
      label: 'Compresseur d’air',
      energy: 'electricity',
      iconKey: 'pump',
    },
    {
      id: 'aspiration_copeaux',
      label: 'Aspiration copeaux',
      energy: 'electricity',
      iconKey: 'air-vent',
      recommended: true,
    },
    {
      id: 'cabine_finition',
      label: 'Cabine de finition / peinture',
      energy: 'both',
      iconKey: 'spray-can',
    },
    {
      id: 'chauffage_atelier',
      label: 'Chauffage atelier',
      energy: 'both',
      iconKey: 'flame',
    },
    {
      id: 'eclairage_atelier',
      label: 'Éclairage atelier',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const SALLE_SPORT: SectorProfile = {
  id: 'salle_sport',
  label: 'Salle de sport / Fitness',
  description: "Vestiaires (ECS, ventilation), climatisation, éclairage, équipements connectés.",
  iconKey: 'dumbbell',
  usages: [
    {
      id: 'ecs_vestiaires',
      label: 'Eau chaude vestiaires / douches',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'vmc_vestiaires',
      label: 'Ventilation / VMC vestiaires',
      energy: 'electricity',
      iconKey: 'air-vent',
      recommended: true,
    },
    {
      id: 'clim_salle',
      label: 'Climatisation / brassage d’air',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'eclairage_salle',
      label: 'Éclairage salle d’entraînement',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'equipement_cardio',
      label: 'Cardio-machines connectées',
      energy: 'electricity',
      iconKey: 'monitor',
    },
    {
      id: 'piscine',
      label: 'Piscine / sauna / hammam',
      energy: 'both',
      iconKey: 'thermometer',
    },
    {
      id: 'sono',
      label: 'Sono + écrans',
      energy: 'electricity',
      iconKey: 'zap',
    },
  ],
};

const BUREAU_TERTIAIRE: SectorProfile = {
  id: 'bureau_tertiaire',
  label: 'Bureau / Cabinet professionnel',
  description: "Postes informatiques, climatisation des bureaux, éclairage, salle serveur éventuelle.",
  iconKey: 'briefcase',
  usages: [
    {
      id: 'bureautique',
      label: 'Postes de travail (PC, écrans)',
      energy: 'electricity',
      iconKey: 'monitor',
      recommended: true,
    },
    {
      id: 'clim_bureaux',
      label: 'Climatisation / réversible',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'chauffage_bureaux',
      label: 'Chauffage des bureaux',
      energy: 'both',
      iconKey: 'thermometer',
      recommended: true,
    },
    {
      id: 'eclairage_bureaux',
      label: 'Éclairage bureaux',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'serveur_datacenter',
      label: 'Salle serveur / baies informatiques',
      energy: 'electricity',
      iconKey: 'server',
    },
    {
      id: 'cuisine_office',
      label: 'Office / coin cuisine (micro-ondes, frigo)',
      energy: 'electricity',
      iconKey: 'chef-hat',
    },
  ],
};

const CABINET_MEDICAL: SectorProfile = {
  id: 'cabinet_medical',
  label: 'Cabinet médical / Paramédical',
  description: "Stérilisation, éclairage médical, climatisation, équipements de soin.",
  iconKey: 'stethoscope',
  usages: [
    {
      id: 'sterilisateur',
      label: 'Stérilisateur / autoclave',
      energy: 'electricity',
      iconKey: 'fire',
      recommended: true,
    },
    {
      id: 'refri_medicaments',
      label: 'Réfrigérateur médicaments / vaccins',
      energy: 'electricity',
      iconKey: 'refrigerator',
    },
    {
      id: 'eclairage_soin',
      label: 'Éclairage opératoire / scialytique',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'clim_cabinet',
      label: 'Climatisation salles de soin',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'imagerie',
      label: 'Imagerie (radio, échographie)',
      energy: 'electricity',
      iconKey: 'monitor',
    },
    {
      id: 'bureautique',
      label: 'Bureautique + serveur',
      energy: 'electricity',
      iconKey: 'monitor',
    },
  ],
};

const EHPAD: SectorProfile = {
  id: 'ehpad',
  label: 'EHPAD / Maison de retraite',
  description: "Chauffage et ECS collectifs, blanchisserie interne, cuisine, climatisation chambres.",
  iconKey: 'building',
  usages: [
    {
      id: 'chauffage_gaz',
      label: 'Chaudière gaz collective',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'ecs_hotel',
      label: 'Eau chaude sanitaire collective',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'clim_chambres',
      label: 'Climatisation chambres',
      energy: 'electricity',
      iconKey: 'wind',
    },
    {
      id: 'cuisine_collective',
      label: 'Cuisine centrale',
      energy: 'both',
      iconKey: 'chef-hat',
      recommended: true,
    },
    {
      id: 'blanchisserie',
      label: 'Blanchisserie interne',
      energy: 'both',
      iconKey: 'washing-machine',
      recommended: true,
    },
    {
      id: 'ascenseur',
      label: 'Ascenseur(s)',
      energy: 'electricity',
      iconKey: 'building',
    },
    {
      id: 'eclairage_communs',
      label: 'Éclairage parties communes',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const CRECHE: SectorProfile = {
  id: 'creche',
  label: 'Crèche / Petite enfance',
  description: "Chauffage et ECS adaptés, biberonnerie, lave-linge, éclairage doux.",
  iconKey: 'baby',
  usages: [
    {
      id: 'chauffage_gaz',
      label: 'Chaudière gaz',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'chauffage_elec',
      label: 'Chauffage électrique',
      energy: 'electricity',
      iconKey: 'zap',
    },
    {
      id: 'ecs_creche',
      label: 'Eau chaude (biberonnerie, douches)',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'cuisine_creche',
      label: 'Cuisine / réchauffage des repas',
      energy: 'both',
      iconKey: 'chef-hat',
    },
    {
      id: 'lave_linge_pro',
      label: 'Lave-linge professionnel',
      energy: 'electricity',
      iconKey: 'washing-machine',
    },
    {
      id: 'eclairage_salles',
      label: 'Éclairage salles d’éveil',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const ECOLE: SectorProfile = {
  id: 'ecole',
  label: 'École / Établissement scolaire',
  description: "Chauffage de bâtiment, cantine éventuelle, éclairage et équipements numériques.",
  iconKey: 'graduation-cap',
  usages: [
    {
      id: 'chauffage_gaz',
      label: 'Chaudière gaz collective',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'ecs_communs',
      label: 'Eau chaude sanitaire',
      energy: 'both',
      iconKey: 'droplet',
    },
    {
      id: 'cuisine_collective',
      label: 'Cantine / restauration scolaire',
      energy: 'both',
      iconKey: 'chef-hat',
    },
    {
      id: 'eclairage_classes',
      label: 'Éclairage salles de classe',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'bureautique',
      label: 'Équipements numériques (PC, TBI)',
      energy: 'electricity',
      iconKey: 'monitor',
    },
    {
      id: 'salle_sport',
      label: 'Gymnase / salle de sport',
      energy: 'both',
      iconKey: 'dumbbell',
    },
  ],
};

const BTP: SectorProfile = {
  id: 'btp_chantier',
  label: 'BTP / Bâtiment',
  description: "Dépôt + atelier + chantiers : compresseur, outillage électroportatif, éclairage de hangar.",
  iconKey: 'hammer',
  usages: [
    {
      id: 'compresseur_air',
      label: 'Compresseur d’air',
      energy: 'electricity',
      iconKey: 'pump',
    },
    {
      id: 'outillage_elec',
      label: 'Outillage électroportatif (recharge)',
      energy: 'electricity',
      iconKey: 'cog',
      recommended: true,
    },
    {
      id: 'eclairage_atelier',
      label: 'Éclairage dépôt / atelier',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'chauffage_atelier',
      label: 'Chauffage du dépôt',
      energy: 'gas',
      iconKey: 'flame',
    },
    {
      id: 'bureautique',
      label: 'Bureau d’accueil / bureautique',
      energy: 'electricity',
      iconKey: 'monitor',
    },
    {
      id: 'recharge_ve',
      label: 'Recharge utilitaires électriques (VE)',
      energy: 'electricity',
      iconKey: 'zap',
    },
  ],
};

const AGRI_GRANDES_CULTURES: SectorProfile = {
  id: 'agri_grandes_cultures',
  label: 'Exploitation grandes cultures',
  description: "Séchage de grains, ventilation des silos, pompage d’irrigation, hangar agricole.",
  iconKey: 'wheat',
  usages: [
    {
      id: 'sechoir_grain',
      label: 'Séchoir à grain',
      energy: 'both',
      iconKey: 'fire',
      recommended: true,
    },
    {
      id: 'ventilation_silo',
      label: 'Ventilation de silos / fosses',
      energy: 'electricity',
      iconKey: 'air-vent',
      recommended: true,
    },
    {
      id: 'pompe_eau',
      label: 'Pompage / irrigation',
      energy: 'electricity',
      iconKey: 'pump',
    },
    {
      id: 'eclairage_hangar',
      label: 'Éclairage hangar / atelier',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
    {
      id: 'outillage_elec',
      label: 'Outillage / soudure atelier',
      energy: 'electricity',
      iconKey: 'cog',
    },
  ],
};

const AGRI_MARAICHAGE: SectorProfile = {
  id: 'agri_maraichage',
  label: 'Maraîchage / Horticulture',
  description: "Serres chauffées, irrigation, chambres froides, éclairage horticole.",
  iconKey: 'sprout',
  usages: [
    {
      id: 'serre_chauffee',
      label: 'Chauffage de serre',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'eclairage_serre',
      label: 'Éclairage horticole (LED)',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
    {
      id: 'pompe_eau',
      label: 'Irrigation / brumisation',
      energy: 'electricity',
      iconKey: 'pump',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambre froide stockage',
      energy: 'electricity',
      iconKey: 'snowflake',
    },
    {
      id: 'ventilation_serre',
      label: 'Ventilation / extraction de serre',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
  ],
};

const AGRI_ELEVAGE_BOVIN: SectorProfile = {
  id: 'agri_elevage_bovin',
  label: 'Élevage bovin (lait / viande)',
  description: "Salle de traite, tank à lait, ventilation des bâtiments, éclairage.",
  iconKey: 'milk',
  usages: [
    {
      id: 'traite_lait',
      label: 'Salle de traite',
      energy: 'electricity',
      iconKey: 'milk',
      recommended: true,
    },
    {
      id: 'tank_lait',
      label: 'Tank à lait réfrigéré',
      energy: 'electricity',
      iconKey: 'refrigerator',
      recommended: true,
    },
    {
      id: 'ecs_traite',
      label: 'Eau chaude lavage salle de traite',
      energy: 'both',
      iconKey: 'droplet',
      recommended: true,
    },
    {
      id: 'ventilation_elevage',
      label: 'Ventilation bâtiment',
      energy: 'electricity',
      iconKey: 'air-vent',
    },
    {
      id: 'eclairage_etable',
      label: 'Éclairage étable / stabulation',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
    {
      id: 'pompe_eau',
      label: 'Pompage abreuvement',
      energy: 'electricity',
      iconKey: 'pump',
    },
  ],
};

const AGRI_ELEVAGE_PORCIN: SectorProfile = {
  id: 'agri_elevage_porcin',
  label: 'Élevage porcin',
  description: "Chauffage maternité / post-sevrage au gaz, ventilation, éclairage régulé.",
  iconKey: 'beef',
  usages: [
    {
      id: 'chauffage_elevage_gaz',
      label: 'Chauffage maternité (radiants gaz)',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'chauffage_elevage_elec',
      label: 'Chauffage électrique (lampes infrarouges)',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
    {
      id: 'ventilation_elevage',
      label: 'Ventilation des salles',
      energy: 'electricity',
      iconKey: 'air-vent',
      recommended: true,
    },
    {
      id: 'eclairage_etable',
      label: 'Éclairage bâtiments',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
    {
      id: 'alimentation_auto',
      label: 'Alimentation automatique',
      energy: 'electricity',
      iconKey: 'cog',
    },
  ],
};

const AGRI_VOLAILLE: SectorProfile = {
  id: 'agri_elevage_volaille',
  label: 'Élevage de volailles',
  description: "Chauffage poussinière, ventilation, éclairage régulé.",
  iconKey: 'egg',
  usages: [
    {
      id: 'chauffage_elevage_gaz',
      label: 'Chauffage poussinière (radiants gaz)',
      energy: 'gas',
      iconKey: 'flame',
      recommended: true,
    },
    {
      id: 'chauffage_elevage_elec',
      label: 'Chauffage électrique (lampes IR)',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
    {
      id: 'ventilation_elevage',
      label: 'Ventilation du bâtiment',
      energy: 'electricity',
      iconKey: 'air-vent',
      recommended: true,
    },
    {
      id: 'eclairage_etable',
      label: 'Éclairage bâtiments (LED régulée)',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'alimentation_auto',
      label: 'Chaînes d’alimentation automatiques',
      energy: 'electricity',
      iconKey: 'cog',
    },
  ],
};

const AGRI_VITICULTURE: SectorProfile = {
  id: 'agri_viticulture',
  label: 'Viticulture / Cave',
  description: "Pressoirs, pompes œnologiques, régulation thermique des cuves, embouteillage.",
  iconKey: 'wine',
  usages: [
    {
      id: 'pressoir_vinification',
      label: 'Pressoirs / pompes œnologiques',
      energy: 'electricity',
      iconKey: 'cog',
      recommended: true,
    },
    {
      id: 'thermoregulation_cuves',
      label: 'Thermorégulation des cuves',
      energy: 'electricity',
      iconKey: 'thermometer',
      recommended: true,
    },
    {
      id: 'chambre_froide',
      label: 'Chambre froide / stabulation à froid',
      energy: 'electricity',
      iconKey: 'snowflake',
    },
    {
      id: 'embouteillage',
      label: 'Chaîne d’embouteillage / étiquetage',
      energy: 'electricity',
      iconKey: 'cog',
    },
    {
      id: 'eclairage_chai',
      label: 'Éclairage chai / caveau',
      energy: 'electricity',
      iconKey: 'lightbulb',
    },
  ],
};

const COMMERCE_DETAIL: SectorProfile = {
  id: 'commerce_detail',
  label: 'Commerce de détail',
  description: "Boutique généraliste : éclairage vitrine, climatisation, bureautique.",
  iconKey: 'building',
  usages: [
    {
      id: 'eclairage_vitrine',
      label: 'Éclairage vitrine + magasin',
      energy: 'electricity',
      iconKey: 'lightbulb',
      recommended: true,
    },
    {
      id: 'clim_local',
      label: 'Climatisation magasin',
      energy: 'electricity',
      iconKey: 'wind',
      recommended: true,
    },
    {
      id: 'chauffage_boutique',
      label: 'Chauffage boutique',
      energy: 'both',
      iconKey: 'thermometer',
      recommended: true,
    },
    {
      id: 'bureautique',
      label: 'Caisse + bureautique',
      energy: 'electricity',
      iconKey: 'monitor',
    },
    {
      id: 'enseigne_lum',
      label: 'Enseigne lumineuse',
      energy: 'electricity',
      iconKey: 'zap',
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*  Catalogues                                                                 */
/* -------------------------------------------------------------------------- */

export const SECTOR_PROFILES: Record<string, SectorProfile> = {
  [BOULANGER.id]: BOULANGER,
  [RESTAURANT.id]: RESTAURANT,
  [RESTAURATION_RAPIDE.id]: RESTAURATION_RAPIDE,
  [TRAITEUR.id]: TRAITEUR,
  [BAR_CAFE.id]: BAR_CAFE,
  [HOTEL.id]: HOTEL,
  [BOUCHERIE.id]: BOUCHERIE,
  [POISSONNERIE.id]: POISSONNERIE,
  [ALIMENTATION_GENERALE.id]: ALIMENTATION_GENERALE,
  [FLEURISTE.id]: FLEURISTE,
  [PHARMACIE.id]: PHARMACIE,
  [COIFFEUR.id]: COIFFEUR,
  [ESTHETIQUE.id]: ESTHETIQUE,
  [PRESSING.id]: PRESSING,
  [GARAGE.id]: GARAGE,
  [STATION_SERVICE.id]: STATION_SERVICE,
  [IMPRIMERIE.id]: IMPRIMERIE,
  [ATELIER_INDUSTRIE.id]: ATELIER_INDUSTRIE,
  [MENUISERIE.id]: MENUISERIE,
  [SALLE_SPORT.id]: SALLE_SPORT,
  [BUREAU_TERTIAIRE.id]: BUREAU_TERTIAIRE,
  [CABINET_MEDICAL.id]: CABINET_MEDICAL,
  [EHPAD.id]: EHPAD,
  [CRECHE.id]: CRECHE,
  [ECOLE.id]: ECOLE,
  [BTP.id]: BTP,
  [AGRI_GRANDES_CULTURES.id]: AGRI_GRANDES_CULTURES,
  [AGRI_MARAICHAGE.id]: AGRI_MARAICHAGE,
  [AGRI_ELEVAGE_BOVIN.id]: AGRI_ELEVAGE_BOVIN,
  [AGRI_ELEVAGE_PORCIN.id]: AGRI_ELEVAGE_PORCIN,
  [AGRI_VOLAILLE.id]: AGRI_VOLAILLE,
  [AGRI_VITICULTURE.id]: AGRI_VITICULTURE,
  [COMMERCE_DETAIL.id]: COMMERCE_DETAIL,
};

/**
 * Mapping code NAF complet (5 caractères, ex. `1071C`) → profil.
 * Priorité absolue : ce mapping est consulté en premier.
 */
export const NAF_CODE_TO_PROFILE: Record<string, string> = {
  '1071A': 'boulanger',
  '1071B': 'boulanger',
  '1071C': 'boulanger',
  '1071D': 'boulanger',

  '5610A': 'restaurant',
  '5610B': 'restaurant',
  '5610C': 'restauration_rapide',
  '5621Z': 'traiteur',
  '5629A': 'traiteur',
  '5629B': 'traiteur',

  '5630Z': 'bar_cafe',

  '5510Z': 'hotel',
  '5520Z': 'hotel',

  '4722Z': 'boucherie_charcuterie',
  '1013A': 'boucherie_charcuterie',
  '1013B': 'boucherie_charcuterie',
  '4723Z': 'poissonnerie',

  '4711A': 'alimentation_generale',
  '4711B': 'alimentation_generale',
  '4711C': 'alimentation_generale',
  '4711D': 'alimentation_generale',
  '4711E': 'alimentation_generale',
  '4711F': 'alimentation_generale',
  '4729Z': 'alimentation_generale',

  '4776Z': 'fleuriste',
  '4773Z': 'pharmacie',

  '9602A': 'coiffeur',
  '9602B': 'esthetique',
  '9604Z': 'esthetique',
  '9601A': 'pressing',
  '9601B': 'pressing',

  '4520A': 'garage',
  '4520B': 'garage',
  '4520Z': 'garage',
  '4730Z': 'station_service',

  '1812Z': 'imprimerie',
  '1813Z': 'imprimerie',
  '1814Z': 'imprimerie',

  '1623Z': 'menuiserie',
  '4332A': 'menuiserie',

  '9311Z': 'salle_sport',
  '9313Z': 'salle_sport',
  '9319Z': 'salle_sport',

  '8621Z': 'cabinet_medical',
  '8622A': 'cabinet_medical',
  '8622B': 'cabinet_medical',
  '8622C': 'cabinet_medical',
  '8623Z': 'cabinet_medical',
  '8690D': 'cabinet_medical',
  '8690E': 'cabinet_medical',
  '8690F': 'cabinet_medical',
  '8710A': 'ehpad',
  '8710B': 'ehpad',
  '8710C': 'ehpad',

  '8891A': 'creche',
  '8891B': 'creche',
  '8510Z': 'ecole',
  '8520Z': 'ecole',
  '8531Z': 'ecole',
  '8532Z': 'ecole',

  '4120A': 'btp_chantier',
  '4120B': 'btp_chantier',
  '4321A': 'btp_chantier',
  '4322A': 'btp_chantier',
  '4322B': 'btp_chantier',
  '4332B': 'btp_chantier',
  '4332C': 'btp_chantier',

  '0111Z': 'agri_grandes_cultures',
  '0119Z': 'agri_grandes_cultures',
  '0113Z': 'agri_maraichage',
  '0125Z': 'agri_maraichage',
  '0128Z': 'agri_maraichage',
  '0130Z': 'agri_maraichage',
  '0141Z': 'agri_elevage_bovin',
  '0142Z': 'agri_elevage_bovin',
  '0143Z': 'agri_elevage_bovin',
  '0144Z': 'agri_elevage_bovin',
  '0145Z': 'agri_elevage_bovin',
  '0146Z': 'agri_elevage_porcin',
  '0147Z': 'agri_elevage_volaille',
  '0121Z': 'agri_viticulture',
  '1102A': 'agri_viticulture',
  '1102B': 'agri_viticulture',
};

/**
 * Fallback par préfixe (3 caractères en priorité, sinon 2 caractères).
 * On y va du plus spécifique au plus général.
 */
export const NAF_PREFIX_TO_PROFILE: Array<{ prefix: string; profileId: string }> = [
  { prefix: '107', profileId: 'boulanger' },
  { prefix: '561', profileId: 'restaurant' },
  { prefix: '562', profileId: 'traiteur' },
  { prefix: '563', profileId: 'bar_cafe' },
  { prefix: '551', profileId: 'hotel' },
  { prefix: '552', profileId: 'hotel' },
  { prefix: '452', profileId: 'garage' },
  { prefix: '477', profileId: 'commerce_detail' },
  { prefix: '478', profileId: 'commerce_detail' },
  { prefix: '472', profileId: 'alimentation_generale' },
  { prefix: '471', profileId: 'alimentation_generale' },
  { prefix: '960', profileId: 'commerce_detail' },
  { prefix: '961', profileId: 'pressing' },
  { prefix: '181', profileId: 'imprimerie' },
  { prefix: '162', profileId: 'menuiserie' },
  { prefix: '931', profileId: 'salle_sport' },
  { prefix: '862', profileId: 'cabinet_medical' },
  { prefix: '871', profileId: 'ehpad' },
  { prefix: '889', profileId: 'creche' },
  { prefix: '852', profileId: 'ecole' },
  { prefix: '853', profileId: 'ecole' },
  { prefix: '412', profileId: 'btp_chantier' },
  { prefix: '433', profileId: 'btp_chantier' },
  { prefix: '432', profileId: 'btp_chantier' },
  { prefix: '431', profileId: 'btp_chantier' },
  { prefix: '011', profileId: 'agri_grandes_cultures' },
  { prefix: '012', profileId: 'agri_maraichage' },
  { prefix: '013', profileId: 'agri_maraichage' },
  { prefix: '014', profileId: 'agri_elevage_bovin' },
  // 2-char fallback
  { prefix: '10', profileId: 'commerce_detail' },
  { prefix: '11', profileId: 'agri_viticulture' },
  { prefix: '13', profileId: 'atelier_industrie' },
  { prefix: '14', profileId: 'atelier_industrie' },
  { prefix: '15', profileId: 'atelier_industrie' },
  { prefix: '16', profileId: 'menuiserie' },
  { prefix: '17', profileId: 'atelier_industrie' },
  { prefix: '18', profileId: 'imprimerie' },
  { prefix: '22', profileId: 'atelier_industrie' },
  { prefix: '23', profileId: 'atelier_industrie' },
  { prefix: '24', profileId: 'atelier_industrie' },
  { prefix: '25', profileId: 'atelier_industrie' },
  { prefix: '26', profileId: 'atelier_industrie' },
  { prefix: '27', profileId: 'atelier_industrie' },
  { prefix: '28', profileId: 'atelier_industrie' },
  { prefix: '29', profileId: 'atelier_industrie' },
  { prefix: '30', profileId: 'atelier_industrie' },
  { prefix: '31', profileId: 'menuiserie' },
  { prefix: '32', profileId: 'atelier_industrie' },
  { prefix: '33', profileId: 'atelier_industrie' },
  { prefix: '41', profileId: 'btp_chantier' },
  { prefix: '42', profileId: 'btp_chantier' },
  { prefix: '43', profileId: 'btp_chantier' },
  { prefix: '45', profileId: 'garage' },
  { prefix: '47', profileId: 'commerce_detail' },
  { prefix: '55', profileId: 'hotel' },
  { prefix: '56', profileId: 'restaurant' },
  { prefix: '58', profileId: 'bureau_tertiaire' },
  { prefix: '59', profileId: 'bureau_tertiaire' },
  { prefix: '60', profileId: 'bureau_tertiaire' },
  { prefix: '61', profileId: 'bureau_tertiaire' },
  { prefix: '62', profileId: 'bureau_tertiaire' },
  { prefix: '63', profileId: 'bureau_tertiaire' },
  { prefix: '64', profileId: 'bureau_tertiaire' },
  { prefix: '65', profileId: 'bureau_tertiaire' },
  { prefix: '66', profileId: 'bureau_tertiaire' },
  { prefix: '68', profileId: 'bureau_tertiaire' },
  { prefix: '69', profileId: 'bureau_tertiaire' },
  { prefix: '70', profileId: 'bureau_tertiaire' },
  { prefix: '71', profileId: 'bureau_tertiaire' },
  { prefix: '72', profileId: 'bureau_tertiaire' },
  { prefix: '73', profileId: 'bureau_tertiaire' },
  { prefix: '74', profileId: 'bureau_tertiaire' },
  { prefix: '75', profileId: 'bureau_tertiaire' },
  { prefix: '77', profileId: 'bureau_tertiaire' },
  { prefix: '78', profileId: 'bureau_tertiaire' },
  { prefix: '79', profileId: 'bureau_tertiaire' },
  { prefix: '80', profileId: 'bureau_tertiaire' },
  { prefix: '81', profileId: 'bureau_tertiaire' },
  { prefix: '82', profileId: 'bureau_tertiaire' },
  { prefix: '85', profileId: 'ecole' },
  { prefix: '86', profileId: 'cabinet_medical' },
  { prefix: '87', profileId: 'ehpad' },
  { prefix: '88', profileId: 'creche' },
  { prefix: '93', profileId: 'salle_sport' },
  { prefix: '94', profileId: 'bureau_tertiaire' },
  { prefix: '95', profileId: 'garage' },
  { prefix: '96', profileId: 'commerce_detail' },
];

/**
 * Normalise un code NAF (supprime espaces, points, met en majuscules).
 * Accepte `1071C`, `10.71C`, `10 71C`, `10.71 C`, etc.
 */
function normalizeNafCode(naf: string | null | undefined): string | null {
  if (!naf) return null;
  const cleaned = naf.replace(/[\s.]/g, '').toUpperCase();
  if (cleaned.length < 2) return null;
  return cleaned;
}

/**
 * Résout un profil sectoriel depuis un code NAF.
 * Retourne `null` si aucun profil ne matche — l'étape Usages sera alors sautée.
 */
export function resolveSectorProfile(
  nafCode: string | null | undefined,
): SectorProfile | null {
  const code = normalizeNafCode(nafCode);
  if (!code) return null;

  const direct = NAF_CODE_TO_PROFILE[code];
  if (direct && SECTOR_PROFILES[direct]) {
    return SECTOR_PROFILES[direct]!;
  }

  for (let len = 4; len >= 2; len--) {
    const prefix = code.slice(0, len);
    const hit = NAF_PREFIX_TO_PROFILE.find((p) => p.prefix === prefix);
    if (hit && SECTOR_PROFILES[hit.profileId]) {
      return SECTOR_PROFILES[hit.profileId]!;
    }
  }

  return null;
}

/**
 * Filtre les usages d'un profil selon le choix d'énergie.
 * Les usages `both` sont toujours conservés.
 */
export function filterUsagesByEnergy(
  profile: SectorProfile,
  energy: 'electricity' | 'gas' | 'both',
): SectorUsage[] {
  if (energy === 'both') return profile.usages;
  return profile.usages.filter((u) => u.energy === energy || u.energy === 'both');
}

/**
 * Type guard : vérifie qu'une chaîne est un `UsageEnergy` valide.
 */
export function isUsageEnergy(value: unknown): value is UsageEnergy {
  return value === 'electricity' || value === 'gas' || value === 'both';
}

/**
 * Retourne les libellés des usages connus à partir des ids stockés en DB.
 * Préserve l'ordre du profil source.
 */
export function getUsageLabels(
  profileId: string | null | undefined,
  usageIds: string[] | null | undefined,
): Array<{ id: string; label: string; energy: UsageEnergy; iconKey: UsageIconKey }> {
  if (!profileId || !usageIds || usageIds.length === 0) return [];
  const profile = SECTOR_PROFILES[profileId];
  if (!profile) return [];
  const selected = new Set(usageIds);
  return profile.usages
    .filter((u) => selected.has(u.id))
    .map((u) => ({ id: u.id, label: u.label, energy: u.energy, iconKey: u.iconKey }));
}
